import React, { useState, useEffect, useRef } from 'react';
import {
  Modal as ModalNewOrder,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  Collapse,
  Switch,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { 
  Cancel, 
  Save, 
  Add, 
  ExpandMore, 
  ExpandLess, 
  Delete, 
  Star,
  StarBorder
} from '@mui/icons-material';
import { checkOrderNumberExists } from '../api';
import dayjs from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const NewOrderModal = ({
  isOpen,
  onClose,
  formData,
  handleFormChange,
  handleCreateOrder,
  clients,
  Cuentas,
  handleCuentaChange,
  selectedClientLocations,
  handleClientChange,
  handleLocationChange,
  bodegas,
  handleBodegaChange,
  products,
  handleProductChange,
  handleAddProduct,
  orderProducts,
  handleRemoveProduct,
}) => {
  const [showProducts, setShowProducts] = useState(false);
  const [selectedProductDescriptions, setSelectedProductDescriptions] = useState({});
  const [productErrors, setProductErrors] = useState({});
  const [orderNumberError, setOrderNumberError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPickup, setIsPickup] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isClienteEspecial, setIsClienteEspecial] = useState(false);
  const [customAddress, setCustomAddress] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [customState, setCustomState] = useState('');
  const [isOrdenEspecial, setIsOrdenEspecial] = useState(false);
  const [customOrigin, setCustomOrigin] = useState('');
  const [customDO, setCustomDO] = useState('');
  const [customPO, setCustomPO] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedOriginLocations, setSelectedOriginLocations] = useState([]);
  const [selectedOriginAddress, setSelectedOriginAddress] = useState('');
  const [selectedOriginCity, setSelectedOriginCity] = useState('');
  const [selectedOriginState, setSelectedOriginState] = useState('');
  const [appointmentTime, setAppointmentTime] = useState(null);
  const orderNumberTimeoutRef = useRef();

  const checkOrderNumberExistsHandler = async (orderNumber) => {
    if (!orderNumber.trim()) {
      setOrderNumberError('');
      return false;
    }
    
    try {
      const data = await checkOrderNumberExists(orderNumber);
      const exists = data.exists;
      
      if (exists) {
        setOrderNumberError('Este número de orden ya existe en el sistema');
      } else {
        setOrderNumberError('');
      }
      
      return exists;
    } catch (error) {
      console.error('Error al verificar el número de orden:', error);
      setOrderNumberError('Error al verificar el número de orden');
      return false;
    }
  };

  const handleOrderNumberChange = (e) => {
    const { value } = e.target;
    handleFormChange(e);
    if (orderNumberTimeoutRef.current) {
      clearTimeout(orderNumberTimeoutRef.current);
    }
    orderNumberTimeoutRef.current = setTimeout(() => {
      if (value.trim()) {
        checkOrderNumberExistsHandler(value);
      } else {
        setOrderNumberError('');
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (orderNumberTimeoutRef.current) {
        clearTimeout(orderNumberTimeoutRef.current);
      }
    };
  }, []);

  const handleClientSelection = (event, newValue) => {
    handleCuentaChange(event, newValue);
    setSelectedClient(newValue);
    
    if (newValue) {
      const clientId = newValue.id ? String(newValue.id) : '';
      const clientNombre = newValue.nombre ? String(newValue.nombre) : '';
      
      const clientProducts = products.filter(product => {
        const productCuenta = product.cuenta ? String(product.cuenta) : '';
        return productCuenta === clientId || productCuenta === clientNombre;
      });
      
      setFilteredProducts(clientProducts);
    } else {
      setFilteredProducts([]);
    }
  };

  const handleProductSelection = (index, newValue) => {
    setProductErrors(prev => ({ ...prev, [index]: '' }));

    if (!newValue) {
      handleProductChange(index, newValue);
      setSelectedProductDescriptions(prev => ({
        ...prev,
        [index]: ''
      }));
      return;
    }

    const isDuplicate = orderProducts.some((op, idx) => 
      idx !== index && 
      op && 
      op.codigo_producto === newValue.codigo_producto
    );

    if (isDuplicate) {
      setProductErrors(prev => ({
        ...prev,
        [index]: `El producto ${newValue.codigo_producto} ya está seleccionado`
      }));
      return;
    }

    handleProductChange(index, newValue);
    setSelectedProductDescriptions(prev => ({
      ...prev,
      [index]: newValue.descripcion
    }));
  };

  const handleOrderTypeChange = (event) => {
    const isPickupValue = event.target.checked;
    setIsPickup(isPickupValue);
    
    handleClientChange(null, null);
    handleLocationChange(null, null);
    handleBodegaChange(null, null);
    
    handleFormChange({ 
      target: { 
        name: 'OrderType', 
        value: isPickupValue ? 'Recolección' : 'Entrega' 
      } 
    });
    
    handleFormChange({
      target: {
        name: 'D_R',
        value: isPickupValue
      }
    });
  };

  const handleConsignatarioSelection = (event, newValue) => {
    handleClientChange(event, newValue);
    
    if (newValue && newValue.customer === "CLIENTE ESPECIAL") {
      setIsClienteEspecial(true);
      setCustomAddress('');
      setCustomCity('');
      setCustomState('');
    } else {
      setIsClienteEspecial(false);
    }
  };
  
  const handleCustomAddressChange = (e) => {
    const { name, value } = e.target;
    
    switch(name) {
      case 'customAddress':
        setCustomAddress(value);
        break;
      case 'customCity':
        setCustomCity(value);
        break;
      case 'customState':
        setCustomState(value);
        break;
      case 'customOrigin':
        setCustomOrigin(value);
        handleFormChange({
          target: {
            name: 'Origin',
            value: value
          }
        });
        break;
      case 'customDO':
        setCustomDO(value);
        handleFormChange({
          target: {
            name: 'Order_number',
            value: value
          }
        });
        break;
      case 'customPO':
        setCustomPO(value);
        handleFormChange({
          target: {
            name: 'Customer_PO',
            value: value
          }
        });
        break;
      default:
        break;
    }
    
    if (['customAddress', 'customCity', 'customState'].includes(name)) {
      handleLocationChange(null, {
        address: name === 'customAddress' ? value : customAddress,
        city: name === 'customCity' ? value : customCity,
        state: name === 'customState' ? value : customState
      });
    }
  };

  const handleOrdenEspecialChange = (event) => {
    setIsOrdenEspecial(event.target.checked);
    
    if (event.target.checked) {
      setCustomDO('');
      setCustomPO('');
      
      handleFormChange({ target: { name: 'Order_number', value: '' } });
      handleFormChange({ target: { name: 'Customer_PO', value: '' } });
    }
  };

  const resetForm = () => {
    handleFormChange({ target: { name: 'Order_number', value: '' } });
    handleFormChange({ target: { name: 'Customer_PO', value: '' } });
    handleFormChange({ target: { name: 'Appointment', value: '' } });
    handleFormChange({ target: { name: 'Appointment_hr', value: '' } });
    handleFormChange({ target: { name: 'OrderType', value: 'Entrega' } });
    handleFormChange({ target: { name: 'D_R', value: false } });
    setIsPickup(false);
    handleProductChange([]);
    setSelectedProductDescriptions({});
    setOrderNumberError('');
    setFilteredProducts([]);
    setSelectedClient(null);
    setIsClienteEspecial(false);
    setCustomAddress('');
    setCustomCity('');
    setCustomState('');
    setIsOrdenEspecial(false);
    setCustomDO('');
    setCustomPO('');
    setCustomOrigin('');
    setAppointmentTime(null);
  };

  useEffect(() => {
    handleFormChange({
      target: {
        name: 'D_R',
        value: false
      }
    });
  }, []);

  useEffect(() => {
    if (products && products.length > 0) {
      console.log("Products data structure sample:", products.slice(0, 3));
      console.log("Total products loaded:", products.length);
    }
  }, [products]);

  const sortedClients = [...clients].sort((a, b) => 
    a.customer.localeCompare(b.customer)
  );
  
  const sortedCuentas = [...Cuentas].sort((a, b) => 
    a.nombre.localeCompare(b.nombre)
  );

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleOriginSelection = (event, newValue) => {
    if (newValue) {
      handleFormChange({
        target: {
          name: 'Origin',
          value: newValue.customer,
        },
      });
    } else {
      handleFormChange({
        target: {
          name: 'Origin',
          value: '',
        },
      });
    }
  };

  const handleSpecialOrderOriginSelection = (event, newValue) => {
    if (newValue) {
      setSelectedOrigin(newValue);
      
      handleFormChange({
        target: {
          name: 'Origin',
          value: newValue.customer,
        },
      });
      
      const originLocations = clients.find(client => client.customer === newValue.customer);
      setSelectedOriginLocations(originLocations ? originLocations.locations : []);
    } else {
      setSelectedOrigin(null);
      setSelectedOriginLocations([]);
      handleFormChange({
        target: {
          name: 'Origin',
          value: '',
        },
      });
    }
  };
  
  const handleOriginLocationChange = (event, value) => {
    if (value) {
      setSelectedOriginAddress(value.address || '');
      setSelectedOriginCity(value.city || '');
      setSelectedOriginState(value.state || '');
    } else {
      setSelectedOriginAddress('');
      setSelectedOriginCity('');
      setSelectedOriginState('');
    }
  };

  const handleTimeChange = (newTime) => {
    setAppointmentTime(newTime);
    
    const formattedTime = newTime ? newTime.format('HH:mm') : '';
    
    handleFormChange({
      target: {
        name: 'Appointment_hr',
        value: formattedTime
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    let orderExists = false;
    if (!isOrdenEspecial) {
      orderExists = await checkOrderNumberExistsHandler(formData.Order_number);
    }
    
    if (orderExists) {
      setIsSubmitting(false);
      return;
    }
    
    if (isPickup) {
      const formDataForSubmit = {
        ...formData,
        Origin: formData.customer,
        customer: formData.Origin
      };
      
      handleCreateOrder(e, formDataForSubmit);
    } else if (isClienteEspecial && !isPickup) {
      const formDataForSubmit = {
        ...formData,
        customerAddress: customAddress,
        customerCity: customCity,
        customerState: customState
      };
      
      handleCreateOrder(e, formDataForSubmit);
    } else if (isOrdenEspecial) {
      const formDataForSubmit = {
        ...formData,
        Order_number: customDO,
        Customer_PO: customPO,
        special: true,
        isSpecialOrder: true,
        special_address: selectedOriginAddress,
        special_city: selectedOriginCity,
        special_state: selectedOriginState
      };
      
      handleCreateOrder(e, formDataForSubmit);
    } else {
      handleCreateOrder(e);
    }
    
    setIsSubmitting(false);
  };

  return (
    <ModalNewOrder open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <Typography variant="h6" gutterBottom>
          Crear Nueva Orden
        </Typography>
        <form onSubmit={handleSubmit}>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2,
            gap: 2
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPickup}
                  onChange={handleOrderTypeChange}
                  color="primary"
                  disabled={isOrdenEspecial}
                />
              }
              label={
                <Typography sx={{ fontWeight: 'bold' }}>
                  {isPickup ? "Recolección" : "Entrega"}
                </Typography>
              }
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: '16px', 
                px: 2,
                flex: 1,
                backgroundColor: isPickup ? 'rgba(25, 118, 210, 0.08)' : 'rgba(46, 125, 50, 0.08)',
                '& .MuiFormControlLabel-label': { 
                  color: isPickup ? 'primary.main' : 'success.main' 
                }
              }}
            />
            
            <Tooltip title="Orden Especial" placement="top">
              <IconButton
                onClick={(e) => handleOrdenEspecialChange({
                  target: { checked: !isOrdenEspecial }
                })}
                color={isOrdenEspecial ? "warning" : "default"}
                disabled={isPickup}
                sx={{ 
                  border: isOrdenEspecial ? '1px solid' : 'none', 
                  borderColor: 'warning.main',
                  borderRadius: '8px',
                  p: 1
                }}
              >
                {isOrdenEspecial ? <Star /> : <StarBorder />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" gap={2}>
            <Autocomplete
              fullWidth
              options={sortedCuentas}
              getOptionLabel={(option) => option.nombre || ''}
              onChange={handleClientSelection}
              renderInput={(params) => <TextField {...params} label="Cliente" required />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>
          <div className='mt-4'></div>

          <Box display="flex" gap={2} mt={2}>
            {isOrdenEspecial ? (
              <TextField
                fullWidth
                label="N° de Orden"
                name="customDO"
                value={customDO}
                onChange={handleCustomAddressChange}
                required
              />
            ) : (
              <TextField
                fullWidth
                label="N° de Orden"
                name="Order_number"
                value={formData.Order_number}
                onChange={handleOrderNumberChange}
                error={Boolean(orderNumberError)}
                helperText={orderNumberError}
                required
              />
            )}
            
            {isOrdenEspecial ? (
              <TextField
                fullWidth
                label="Factura"
                name="customPO"
                value={customPO}
                onChange={handleCustomAddressChange}
                required
              />
            ) : (
              <TextField
                fullWidth
                label="Factura"
                name="Customer_PO"
                value={formData.Customer_PO}
                onChange={handleFormChange}
                required
              />
            )}
          </Box>

          {isPickup && !isOrdenEspecial ? (
            <>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={bodegas}
                  getOptionLabel={(option) => option.nombre}
                  onChange={handleBodegaChange}
                  renderInput={(params) => <TextField {...params} label="Almacén Destino (Consignatario)" required />}
                />
                <Autocomplete
                  fullWidth
                  options={sortedClients}
                  getOptionLabel={(option) => option.customer}
                  onChange={handleClientChange}
                  renderInput={(params) => <TextField {...params} label="Origen de Recolección" required />}
                />
              </Box>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={selectedClientLocations}
                  getOptionLabel={(option) => option.address}
                  onChange={handleLocationChange}
                  renderInput={(params) => <TextField {...params} label="Dirección de Recolección" required />}
                />
              </Box>
            </>
          ) : (
            <>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={sortedClients}
                  getOptionLabel={(option) => option.customer}
                  onChange={(event, newValue) => handleConsignatarioSelection(event, newValue)}
                  renderInput={(params) => <TextField {...params} label="Consignatario" required />}
                />
                
                {isClienteEspecial ? (
                  <TextField
                    fullWidth
                    label="Ubicación (Dirección)"
                    name="customAddress"
                    value={customAddress}
                    onChange={handleCustomAddressChange}
                    required
                  />
                ) : (
                  <Autocomplete
                    fullWidth
                    options={selectedClientLocations}
                    getOptionLabel={(option) => option.address}
                    onChange={handleLocationChange}
                    renderInput={(params) => <TextField {...params} label="Destinatario" required />}
                  />
                )}
              </Box>
              
              {isClienteEspecial && (
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    fullWidth
                    label="Ciudad"
                    name="customCity"
                    value={customCity}
                    onChange={handleCustomAddressChange}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Estado"
                    name="customState"
                    value={customState}
                    onChange={handleCustomAddressChange}
                    required
                  />
                </Box>
              )}
              
              <Box display="flex" gap={2} mt={2}>
                {isOrdenEspecial ? (
                  <>
                    <Autocomplete
                      fullWidth
                      options={sortedClients}
                      getOptionLabel={(option) => option.customer}
                      onChange={handleSpecialOrderOriginSelection}
                      renderInput={(params) => <TextField {...params} label="Origen de Carga" required />}
                    />
                  </>
                ) : (
                  <Autocomplete
                    fullWidth
                    options={bodegas}
                    getOptionLabel={(option) => option.nombre}
                    onChange={handleBodegaChange}
                    renderInput={(params) => <TextField {...params} label="Origen de Carga" required />}
                  />
                )}
              </Box>
              
              {isOrdenEspecial && selectedOrigin && (
                <Box display="flex" gap={2} mt={2}>
                  <Autocomplete
                    fullWidth
                    options={selectedOriginLocations}
                    getOptionLabel={(option) => option.address}
                    onChange={handleOriginLocationChange}
                    renderInput={(params) => <TextField {...params} label="Ubicación de Origen" required />}
                  />
                </Box>
              )}
            </>
          )}

          <Box display="flex" gap={2} mt={2}>
            <TextField
              fullWidth
              label={isPickup ? "Fecha de Entrega" : "Fecha de Entrega"}
              type="date"
              name="Appointment"
              value={formData.Appointment}
              onChange={handleFormChange}
              required
              InputLabelProps={{ shrink: true }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label={isPickup ? "Hora de Entrega" : "Hora de Entrega"}
                value={appointmentTime}
                onChange={handleTimeChange}
                ampm={false}
                minutesStep={5}
                className="full-width"
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    required: true
                  } 
                }}
                format="HH:mm"
              />
            </LocalizationProvider>
          </Box>

          <Button
            startIcon={showProducts ? <ExpandLess /> : <ExpandMore />}
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => setShowProducts(!showProducts)}
          >
            {showProducts ? 'Ocultar Productos' : 'Añadir Productos'}
          </Button>
          <Collapse in={showProducts} sx={{ mt: 2 }}>
            {orderProducts.map((orderProduct, index) => (
              <Box key={index} display="flex" flexDirection="column" gap={1} mt={2} p={2} sx={{ border: '1px solid #ccc', borderRadius: '4px' }}>
                <Box display="flex" gap={2}>
                  <Autocomplete
                    fullWidth
                    options={filteredProducts}
                    getOptionLabel={(option) => option.codigo_producto || ''}
                    onChange={(event, value) => handleProductSelection(index, value)}
                    noOptionsText={selectedClient ? "No hay productos para este cliente" : "Seleccione un cliente primero"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Producto"
                        required
                        error={Boolean(productErrors[index])}
                        helperText={productErrors[index] || (!selectedClient ? 'Seleccione un cliente primero' : '')}
                        disabled={!selectedClient}
                      />
                    )}
                  />
                  <TextField
                    type="number"
                    label="Cantidad"
                    fullWidth
                    value={orderProduct.product_qt || ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value === '' || !isNaN(value)) {
                        handleProductChange(index, value === '' ? '' : parseInt(value, 10), 'product_qt');
                      }
                    }}
                    onFocus={(event) => event.target.select()}
                    inputProps={{ min: 0 }}
                    required
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveProduct(index)}
                    sx={{ alignSelf: 'center' }}
                  >
                    <Delete />
                  </IconButton>
                </Box>

                {selectedProductDescriptions[index] && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    <strong>Descripción:</strong> {selectedProductDescriptions[index]}
                  </Typography>
                )}
              </Box>
            ))}

            <Button
              startIcon={<Add />}
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleAddProduct}
              disabled={!selectedClient || filteredProducts.length === 0}
            >
              {!selectedClient ? 'Seleccione un cliente primero' : 
               filteredProducts.length === 0 ? 'No hay productos disponibles' : 'Añadir Producto'}
            </Button>
          </Collapse>
          
          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button variant="contained" color="error" startIcon={<Cancel />} onClick={handleClose}>
              Cancelar
            </Button>

            <Button 
              variant="contained" 
              color="success" 
              startIcon={<Save />} 
              type="submit"
              disabled={isSubmitting || (!isOrdenEspecial && Boolean(orderNumberError))}
            >
              Guardar
            </Button>
          </Box>
        </form>
      </Box>
    </ModalNewOrder>
  );
};

export default NewOrderModal;